import { useAtomValue } from 'jotai';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import type { Tenant } from '@core/Entities/App/Tenants.entity';
import type { FilterKey } from '@core/Entities/Search/QueryParams.entity';
import { getSlugParamFromFilter } from '@core/Services/Search/Utils/GetSlugParamFromFilter';
import { getFilterFromArray } from '@core/Utils/SearchFilters/GetFilterFromArray';
import { createFiltersLinkForFilterValue } from '@plp/Utils/Filters/CreateFiltersLinkForFilterValue';
import { createFiltersLinkForRangeFilterValue } from '@plp/Utils/Filters/CreateFiltersLinkForRangeFilterValue';
import { shouldDisplayFilterOptionLink } from '@plp/Utils/Filters/ShouldDisplayFilterOptionLink';
import { useGetRoot } from '@search/Hooks/UseGetRoot';

const ESSENTIAL_FACETS = ['make', 'model', 'variant-type'];

export const useFilterLink = ({
  filterId,
  partnerFilterId,
  isPlpFiltersSeoEnabled,
  tenant,
}: {
  filterId: FilterKey;
  partnerFilterId?: FilterKey;
  isPlpFiltersSeoEnabled: boolean;
  tenant: Tenant | undefined;
}) => {
  const root = useGetRoot();
  const filtersList = useAtomValue(filtersListAtom);
  const filter = getFilterFromArray(filterId, filtersList);
  const partnerFilter = partnerFilterId && getFilterFromArray(partnerFilterId, filtersList);
  const selectedOptions = new Set(filter?.value || []);
  const selected = new Set(selectedOptions);

  const createFlrtLinkArgs = {
    filters: filtersList,
    tenant,
    root,
  };
  const createLinkForValue = partnerFilterId
    ? createFiltersLinkForRangeFilterValue({
        ...createFlrtLinkArgs,
        filterIds: [filterId, partnerFilterId],
      })
    : createFiltersLinkForFilterValue({
        ...createFlrtLinkArgs,
        filterId,
      });

  const numExtraFacetsWithActiveFilters = filtersList
    .map(filterItem => getSlugParamFromFilter(filterItem.id, root))
    .filter(filterItem => !!filterItem && !ESSENTIAL_FACETS.includes(filterItem)).length;

  const getLinkForFilterValue = ({
    filterValue,
    resultCount,
  }: {
    filterValue: string;
    resultCount: number;
  }): {
    href: string;
    nofollow: boolean;
  } | null => {
    if (!isPlpFiltersSeoEnabled) return null;

    let isSelected = selected.has(filterValue);
    let hasOptSelected = selectedOptions.size > 0;
    //Things work a little different for range filters
    if (partnerFilter) {
      isSelected = selected.has(filterValue) && partnerFilter.value === filterValue;
      // want links to appear even if one is active
      hasOptSelected = false;
    }

    const shouldDisplay = shouldDisplayFilterOptionLink({
      numExtraFacetsWithActiveFilters,
      isSelected,
      resultCount,
      categoryHasOptionSelected: hasOptSelected,
      root,
    });

    if (shouldDisplay === 'omit') return null;

    const href = createLinkForValue(filterValue);

    const hasFilterIdAsSearchParam = new URLSearchParams(href.split('?')[1] ?? '').has(filterId);
    if (hasFilterIdAsSearchParam) return null;

    if (filterId === 'color' && filterValue === 'other') return null;

    const hrefWithoutSearchParams = href.split('?')[0];

    return {
      href: hrefWithoutSearchParams,
      nofollow: shouldDisplay === 'no-follow',
    };
  };

  return {
    /**
     * Given a filter value and the amount of results it has,
     * returns a link that adds or removes the filter value.
     */
    getLinkForFilterValue,
  };
};
